<template>
  <div class="p-exam-list">
    <!-- ExamList 는 부모(AcademicEvaluations) 의 ACard 안에 렌더되므로
         자체 카드 chrome 없이 헤더 + 테이블만 출력 (AcademicClasses 와 동일 톤) -->
    <div class="p-exam-list__head">
      <div>
        <div class="p-exam-list__title">시험 목록</div>
        <div class="p-exam-list__sub">총 {{ totalCount }}건</div>
      </div>
    </div>

    <div v-if="!hasData && isLoading" class="p-loader"><div class="p-spinner" /></div>

    <a-empty v-else-if="!filteredExams.length" title="시험이 없습니다" sub="새 시험을 생성하거나 필터를 변경해보세요." />

    <div v-else style="position: relative;">
      <a-table :columns="examCols" :rows="paginatedExams" row-key="examId">
        <template #cell-no="{ index }">
          <span class="p-mono">{{ totalCount - ((currentPage - 1) * itemsPerPage + index) }}</span>
        </template>
        <template #cell-name="{ row }">
          <div class="p-exam-list__name">
            <div class="p-exam-list__name-line">
              <span class="p-exam-list__name-text">{{ row?.examName || '시험 이름 없음' }}</span>
              <a-pill v-if="row?.isShared" kind="info" title="다른 사용자와 공유됨">공유됨</a-pill>
              <a-pill v-if="row?.sharedFrom" :title="'출처: ' + row.sharedFrom">복사본</a-pill>
            </div>
            <div v-if="row?.description || row?.sharedFrom" class="p-exam-list__desc">
              {{ row?.description || '' }}
              <span v-if="row?.sharedFrom">(출처: {{ row.sharedFrom }})</span>
            </div>
          </div>
        </template>
        <template #cell-status="{ row }">
          <a-pill :kind="statusPillKind(row?.status || 'draft')">{{ formatExamStatus(row?.status || 'draft') }}</a-pill>
        </template>
        <template #cell-schedule="{ row }">
          <div class="p-exam-list__schedule">
            <div class="p-mono">{{ formatDate(row?.startTimeUTC, row?.timeZone) }}</div>
            <div class="p-cell-sub">
              {{ formatTimeRange(row?.startTimeUTC, row?.endTimeUTC, row?.timeZone) }} · {{ row?.timeZone || 'Asia/Seoul' }}
            </div>
          </div>
        </template>
        <template #cell-questions="{ row }">
          <span class="p-mono">{{ getTotalQuestions(row) }}개</span>
        </template>
        <template #cell-duration="{ row }">
          <span class="p-mono">{{ row?.duration || 0 }}분</span>
        </template>
        <template #cell-actions="{ row }">
          <dropdown-menu
            :button-text="''"
            :button-icon="'more_vert'"
            button-class="p-icon-btn"
            :items="getDropdownItems(row)"
          />
        </template>
      </a-table>
      <div v-show="isLoading" class="p-loading-overlay"><div class="p-spinner" /></div>
    </div>

    <!-- Pagination footer -->
    <div v-if="hasData" class="p-exam-list__footer">
      <span class="p-exam-list__count">
        총 {{ totalCount }}개 중 {{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage, totalCount) }}개 표시
      </span>
      <pagination v-model:current-page="currentPage" :total-pages="totalPages" />
    </div>

    <!-- Share Exam Modal — Pineapple -->
    <a-modal :open="showShareModal" title="시험 공유하기" sub="선택한 사용자에게 시험을 공유합니다" size="lg" @close="closeShareModal">
      <!-- 시험 정보 -->
      <div class="p-share-card">
        <div class="p-share-card__title">{{ shareSourceExam?.examName }}</div>
        <div class="p-share-card__meta">
          <span class="p-mono">{{ shareSourceExam?.duration || 0 }}분</span>
          <span class="p-mono">· 문제 {{ getTotalQuestions(shareSourceExam) }}개</span>
        </div>
      </div>

      <!-- 공유 대상자 입력 -->
      <div class="p-field" style="margin-top: 16px;">
        <label class="p-field__label">공유 대상자 <span class="p-field__req">*</span></label>
        <div class="p-share-input">
          <input
            type="email"
            class="p-input"
            v-model="recipientEmailInput"
            placeholder="이메일 주소 입력 (예: teacher@school.com)"
            @keyup.enter="addRecipient"
            :disabled="isSharing"
          />
          <a-btn kind="primary" :disabled="!recipientEmailInput.trim() || isSharing" @click="addRecipient">추가</a-btn>
        </div>
        <div class="p-field__hint">시험을 공유받을 사용자의 이메일을 입력하세요</div>
      </div>

      <!-- 수신자 목록 -->
      <div v-if="shareRecipients.length > 0" class="p-recipients">
        <div class="p-recipients__list">
          <span v-for="(email, index) in shareRecipients" :key="index" class="p-recipient-chip">
            <span class="p-recipient-chip__avatar">{{ email.charAt(0).toUpperCase() }}</span>
            <span class="p-recipient-chip__email">{{ email }}</span>
            <button type="button" class="p-recipient-chip__x" @click="removeRecipient(index)" :disabled="isSharing">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </span>
        </div>
        <a-pill kind="ok" style="margin-top: 8px;">✓ {{ shareRecipients.length }}명 선택됨</a-pill>
      </div>
      <div v-else class="p-share-empty">
        <div class="p-share-empty__title">공유할 대상자를 추가해주세요</div>
        <div class="p-share-empty__sub">최소 1명 이상 필요합니다</div>
      </div>

      <!-- 안내사항 -->
      <div class="p-alert p-alert--warn" style="margin-top: 16px;">
        <div class="p-alert__title">공유 안내</div>
        <ul class="p-alert__list">
          <li>지정된 사용자만 시험을 내려받을 수 있습니다.</li>
          <li>시험 문제와 정답이 모두 공유됩니다.</li>
          <li>공유 대상자는 나중에 추가하거나 수정할 수 있습니다.</li>
        </ul>
      </div>

      <div v-if="shareError" class="p-alert p-alert--err" style="margin-top: 12px;">
        <div class="p-alert__title">오류</div>
        <div class="p-alert__body">{{ shareError }}</div>
      </div>

      <template #foot>
        <a-btn kind="ghost" @click="closeShareModal">취소</a-btn>
        <a-btn kind="primary" :disabled="isSharing || shareRecipients.length === 0" @click="confirmShareExam">
          {{ isSharing ? '공유 중...' : `${shareRecipients.length}명에게 공유하기` }}
        </a-btn>
      </template>
    </a-modal>

    <!-- Copy Exam Modal — Pineapple -->
    <a-modal :open="showCopyModal" title="시험 복사" :sub="copySourceExam ? `${copySourceExam.examName} 을(를) 복사합니다` : ''" size="md" @close="closeCopyModal">
      <div class="p-field">
        <label class="p-field__label">새 시험 이름 <span class="p-field__req">*</span></label>
        <input
          type="text"
          v-model="newExamName"
          class="p-input"
          placeholder="복사된 시험의 이름을 입력하세요"
          @keyup.enter="confirmCopyExam"
          ref="newExamNameInput"
        />
        <div v-if="copyError" class="p-field__err">{{ copyError }}</div>
      </div>

      <template #foot>
        <a-btn kind="ghost" @click="closeCopyModal">취소</a-btn>
        <a-btn kind="primary" :disabled="!newExamName.trim() || isCopying" @click="confirmCopyExam">
          {{ isCopying ? '복사 중...' : '복사' }}
        </a-btn>
      </template>
    </a-modal>

    <!-- New Exam Detail Modal -->
    <exam-detail-modal
      :visible="showPreview"
      :exam="previewExam"
      :versions="previewVersions"
      mode="preview"
      :show-questions="true"
      @close="closePreview"
    />

    <!-- Old Paper Style Preview Modal (commented out but kept for reference) -->
    <!--
    <div v-if="showPaperPreview" class="exam-preview-modal" @click.self="closePaperPreview">
      ... (original paper preview code) ...
    </div>
    -->
  </div>
</template>

<script>
import { computed, ref, watch, getCurrentInstance, nextTick } from 'vue'
import { useExam } from '@/composables/useExam'
import { formatKoreanDate, formatKoreanTime, formatTimeRangeKorean, formatUtcToTimeZone } from '@/utils/datetime'
import { hasHtmlContent } from '@/utils/html'
import { getExamVersionKeys, buildPreviewVersion } from '@/utils/examPreview'
import DropdownMenu from '@/components/DropdownMenu.vue'
import Pagination from '@/components/Pagination.vue'
import ExamDetailModal from './ExamDetailModal.vue'

import ATable from '@/components/Pineapple/ATable.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import AModal from '@/components/Pineapple/AModal.vue';

export default {
  name: 'ExamList',
  components: {
    DropdownMenu,
    Pagination,
    ExamDetailModal,
    ATable, APill, ABtn, AEmpty, AModal,
  },
  props: {
    filter: {
      type: String,
      default: 'all'
    },
    creatorFilter: {
      type: String,
      default: ''
    }
  },
  emits: ['select', 'edit', 'start-session', 'view-results', 'share-success'],
  setup(props, ctx) {
    const {
      exams,
      isLoading,
      fetchExams,
      deleteExam: deleteExamAction,
      copyExam: copyExamAction,
      shareExam: shareExamAction,
      formatExamStatus,
      getStatusColor
    } = useExam()

    // Pagination state
    const currentPage = ref(1)
    const itemsPerPage = ref(10)

    // Computed
    const filteredExams = computed(() => {
      // Filter out any undefined or null values first
      let validExams = (exams.value || []).filter(exam => exam && exam.examId)

      // 생성자 필터가 있으면 적용 (내 시험만 표시)
      if (props.creatorFilter) {
        validExams = validExams.filter(exam => exam.creatorEmail === props.creatorFilter)
      }

      // 상태 필터 적용
      if (props.filter === 'all') return validExams
      return validExams.filter(exam => exam.status === props.filter)
    })
    const hasData = computed(() => (filteredExams.value || []).length > 0)

    const totalCount = computed(() => (filteredExams.value || []).length)
    const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / itemsPerPage.value)))
    const paginatedExams = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value
      return (filteredExams.value || []).slice(start, start + itemsPerPage.value)
    })

    // 필터 변경 시 1페이지로 이동
    watch(() => [props.filter, props.creatorFilter], () => {
      currentPage.value = 1
    })

    // 데이터 갱신으로 totalPages가 줄었을 때 currentPage 보정
    watch(totalPages, (newTotal) => {
      if (currentPage.value > newTotal) {
        currentPage.value = newTotal
      }
    })

    // fetchExams 호출 시 공통 파라미터 (본인 시험만 — 스토어가 모든 페이지를 받는다)
    // creator_email 은 항상 전달 — 비어있으면 어떤 시험도 가져오지 않도록 강제
    const buildFetchParams = () => ({
      creator_email: props.creatorFilter || ''
    })

    const instance = getCurrentInstance()
    const axios = instance?.proxy?.$axios
    const CONF = instance?.proxy?.$CONF

    // Copy modal state
    const showCopyModal = ref(false)
    const copySourceExam = ref(null)
    const newExamName = ref('')
    const isCopying = ref(false)
    const copyError = ref('')
    const newExamNameInput = ref(null)
    
    // Share modal state
    const showShareModal = ref(false)
    const shareSourceExam = ref(null)
    const isSharing = ref(false)
    const shareError = ref('')
    const shareRecipients = ref([])
    const recipientEmailInput = ref('')
    
    // Preview state
    const showPreview = ref(false)
    const previewExam = ref(null)
    // 버전(A형·B형)별 문항 — [{ key, label, questions, questionGroups }]
    const previewVersions = ref([])

    // Methods
    const getStatusBadgeClass = (status) => {
      const color = getStatusColor(status)
      return `badge bg-gradient-${color}`
    }

    /** Pineapple APill kind 매핑 */
    const statusPillKind = (status) => {
      if (status === 'completed') return 'info'
      if (status === 'active' || status === 'in_progress') return 'ok'
      if (status === 'scheduled') return 'warn'
      if (status === 'draft') return 'default'
      return 'default'
    }

    /** ATable columns 정의 */
    const examCols = [
      { key: 'no',        label: '#',        width: '50px',  align: 'right' },
      { key: 'name',      label: '시험명' },
      { key: 'status',    label: '상태',     width: '100px' },
      { key: 'schedule',  label: '일정',     width: '220px' },
      { key: 'questions', label: '문제수',   width: '90px',  align: 'right' },
      { key: 'duration',  label: '시간',     width: '80px',  align: 'right' },
      { key: 'actions',   label: '',         width: '40px',  align: 'center' },
    ]

    const getTotalQuestions = (exam) => {
      if (!exam) return 0
      if (typeof exam.totalQuestions === 'number') return exam.totalQuestions
      if (!exam.questionDistribution) return 0
      return Object.values(exam.questionDistribution)
        .reduce((sum, dist) => sum + (dist?.count || 0), 0)
    }

    // UTC 시간을 타임존에 맞게 변환하여 표시
    const formatDate = (utcTime, timeZone) => {
      if (!utcTime) return ''
      const localTime = formatUtcToTimeZone(utcTime, timeZone || 'Asia/Seoul')
      return formatKoreanDate(localTime)
    }
    const formatTime = (datetime) => formatKoreanTime(datetime)
    const formatTimeRange = (startUtc, endUtc, timeZone) => {
      if (!startUtc || !endUtc) return ''
      const tz = timeZone || 'Asia/Seoul'
      const startLocal = formatUtcToTimeZone(startUtc, tz)
      const endLocal = formatUtcToTimeZone(endUtc, tz)
      return formatTimeRangeKorean(startLocal, endLocal)
    }

    // 시험 스냅샷(questions-full)에 읽기 지문이 비어 오면 문제은행 그룹('문제 그룹 상세'와 같은 데이터)에서 채운다.
    // 미리보기를 연 뒤 채우므로 화면은 기다리지 않는다. 반응형 배열을 넘겨야 채운 값이 화면에 반영된다.
    // 여러 버전(A형·B형)에 같은 그룹이 있으면 한 번만 조회해 모두 채운다.
    const fillReadingScriptsFromBank = (groups) => {
      const sameGroupsById = new Map()
      groups
        .filter(group => group.groupId !== 'ungrouped' && !group.readingScript)
        .forEach(group => {
          if (!sameGroupsById.has(group.groupId)) sameGroupsById.set(group.groupId, [])
          sameGroupsById.get(group.groupId).push(group)
        })
      sameGroupsById.forEach(async (sameGroups, groupId) => {
        try {
          const res = await axios({
            method: 'get',
            url: `/questions/groups/${groupId}`,
            api_url: CONF.ACHIEVE_API_URL,
            loadingBar: false
          })
          if (hasHtmlContent(res?.data?.readingScript)) {
            sameGroups.forEach(group => { group.readingScript = res.data.readingScript })
          }
        } catch {
          // 문제은행에서 지워진 그룹 등은 스냅샷대로 둔다
        }
      })
    }

    // 응답 본문을 문항 배열로 (배열, 또는 { items } / { results })
    const toSnapshotItems = (data) => (Array.isArray(data) ? data : (data?.items || data?.results || []))
    const toIdList = (ids) => (Array.isArray(ids) ? ids : [])

    // 버전(A형·B형)마다 스냅샷을 따로 받아 따로 묶는다 — 모달이 탭으로 보여 준다(docs/시험_관리_API.md 16절).
    // 2026-10-15 전에는 두 버전의 응답을 한 배열로 합쳐, 그룹 보기에 같은 문항이 두 번 그려지고 B형의 순서는 보이지 않았다.
    const openPreview = async (exam) => {
      previewExam.value = exam
      previewVersions.value = []

      try {
        if (exam?.examId && axios && CONF) {
          const versionKeys = getExamVersionKeys(exam)
          const results = await Promise.allSettled(
            versionKeys.map(version =>
              axios({
                method: 'get',
                url: `/exams/${exam.examId}/questions-full`,
                api_url: CONF.ACHIEVE_API_URL,
                loadingBar: true,
                params: { version }
              })
            )
          )

          // 한 버전이 실패해도 받은 버전은 보여 준다. 스냅샷이 빈 배열로 온 버전은 탭을 만들지 않는다
          // (B형 스냅샷은 B형 순서로 따로 오고, 스냅샷이 빈 시험은 개발·운영 모두 없다 — 2026-10-15 성취도 백엔드 회신)
          const fetched = []
          results.forEach((result, i) => {
            const key = versionKeys[i]
            if (result.status !== 'fulfilled') {
              console.error(`Failed to load exam preview (version ${key}):`, result.reason)
              return
            }
            const questionIds = toIdList(exam?.versions?.[key]?.questionIds)
            fetched.push({
              key,
              items: toSnapshotItems(result.value?.data),
              // 목록 항목의 versions[key].questionIds 는 스냅샷과 같은 순서(같은 목록에서 함께 만듦). 예전 형식(exam.questionIds)은 A 에만
              questionIds: questionIds.length > 0 || key !== 'A' ? questionIds : toIdList(exam?.questionIds)
            })
          })

          previewVersions.value = fetched
            .filter(v => v.items.length > 0)
            .map(v => buildPreviewVersion(v))

          fillReadingScriptsFromBank(previewVersions.value.flatMap(v => v.questionGroups))
        }
      } catch (error) {
        console.error('Failed to load questions for preview:', error)
        previewVersions.value = []
      }

      showPreview.value = true
    }

    const copyExam = async (exam) => {
      copySourceExam.value = exam
      newExamName.value = `${exam.examName} (복사본)`
      copyError.value = ''
      showCopyModal.value = true
      
      // Focus input field after modal shows
      // eslint-disable-next-line no-undef
      await nextTick()
      newExamNameInput.value?.focus()
      newExamNameInput.value?.select()
    }

    const shareExam = async (exam) => {
      shareSourceExam.value = exam
      shareError.value = ''
      showShareModal.value = true
    }
    
    const closeShareModal = () => {
      showShareModal.value = false
      shareSourceExam.value = null
      shareError.value = ''
      isSharing.value = false
      shareRecipients.value = []
      recipientEmailInput.value = ''
    }

    // 이메일 유효성 검사
    const isValidEmail = (email) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    }

    // 공유 대상자 추가
    const addRecipient = () => {
      const email = recipientEmailInput.value.trim().toLowerCase()

      if (!email) {
        return
      }

      if (!isValidEmail(email)) {
        shareError.value = '유효한 이메일 주소를 입력해주세요.'
        return
      }

      // 중복 확인
      if (shareRecipients.value.includes(email)) {
        shareError.value = '이미 추가된 이메일입니다.'
        return
      }

      // 자기 자신에게 공유 방지
      const currentUserEmail = localStorage.getItem('userEmail') ||
        JSON.parse(localStorage.getItem('user') || '{}')?.email ||
        JSON.parse(localStorage.getItem('userInfo') || '{}')?.email || ''

      if (email === currentUserEmail.toLowerCase()) {
        shareError.value = '자기 자신에게는 공유할 수 없습니다.'
        return
      }

      shareRecipients.value.push(email)
      recipientEmailInput.value = ''
      shareError.value = ''
    }

    // 공유 대상자 제거
    const removeRecipient = (index) => {
      shareRecipients.value.splice(index, 1)
    }
    
    const confirmShareExam = async () => {
      // 공유 대상자 검증
      if (shareRecipients.value.length === 0) {
        shareError.value = '공유 대상자를 최소 1명 이상 추가해주세요.'
        return
      }

      isSharing.value = true
      shareError.value = ''

      try {
        // 지정 공유 방식: examId와 recipients 배열 필요
        const shareData = {
          examId: shareSourceExam.value.examId,
          recipients: shareRecipients.value
        }

        // Use the shareExam action from useExam
        const shareId = await shareExamAction(shareData)
        console.log('Share created:', shareId)

        // Show success message and close modal
        alert(`"${shareSourceExam.value.examName}" 시험이 ${shareRecipients.value.length}명에게 성공적으로 공유되었습니다.`)

        // Emit share-success event to parent
        ctx.emit('share-success', {
          examId: shareSourceExam.value.examId,
          examName: shareSourceExam.value.examName,
          shareId: shareId,
          recipients: shareRecipients.value
        })

        closeShareModal()

        // Refresh exam list to show updated share status
        await fetchExams(buildFetchParams())

      } catch (error) {
        console.error('Failed to share exam:', error)
        shareError.value = error.response?.data?.detail || error.message || '시험 공유에 실패했습니다. 다시 시도해주세요.'
      } finally {
        isSharing.value = false
      }
    }
    
    
    const closeCopyModal = () => {
      showCopyModal.value = false
      copySourceExam.value = null
      newExamName.value = ''
      copyError.value = ''
      isCopying.value = false
    }
    
    const confirmCopyExam = async () => {
      // closeCopyModal 이 newExamName 을 비우므로 알림에 쓸 이름을 먼저 잡아 둔다
      const examName = newExamName.value.trim()
      if (!examName) {
        copyError.value = '시험 이름을 입력해주세요.'
        return
      }
      
      isCopying.value = true
      copyError.value = ''
      
      try {
        await copyExamAction(copySourceExam.value.examId, examName)
        // 목록 새로고침은 실패해도 throw 하지 않는다(스토어가 이전 목록을 둠) — 복사 실패로 보이지 않게
        await fetchExams(buildFetchParams())
        closeCopyModal()
        alert(`"${examName}" 시험이 성공적으로 복사되었습니다.`)
      } catch (error) {
        console.error('Failed to copy exam:', error)
        copyError.value = error.response?.data?.detail || '시험 복사에 실패했습니다. 다시 시도해주세요.'
      } finally {
        isCopying.value = false
      }
    }

    const closePreview = () => {
      showPreview.value = false
      previewExam.value = null
      previewVersions.value = []
    }

    const deleteExam = async (exam) => {
      if (confirm(`정말로 "${exam.examName}" 시험을 삭제하시겠습니까?`)) {
        try {
          await deleteExamAction(exam.examId)
          await fetchExams(buildFetchParams())
        } catch (error) {
          console.error('Failed to delete exam:', error)
          alert('시험 삭제에 실패했습니다.')
        }
      }
    }

    const changeStatusToScheduled = async (exam) => {
      const confirmMessage = `정말로 "${exam.examName}" 시험을 예정으로 변경하시겠습니까?\n\n⚠️ 주의: 예정으로 변경하면 학생들에게 알림이 발송됩니다.`

      if (confirm(confirmMessage)) {
        try {
          await axios({
            method: 'patch',
            url: `/exams/${exam.examId}/status?status=scheduled`,
            api_url: CONF.ACHIEVE_API_URL,
            loadingBar: true
          })
          await fetchExams(buildFetchParams())
          alert('시험 상태가 예정으로 변경되었습니다.')

          // 해당 클래스 학생들에게 시험 예정 알림 발송
          sendExamScheduledNotification(exam)
        } catch (error) {
          console.error('Failed to change status to scheduled:', error)
          alert('시험 상태 변경에 실패했습니다.')
        }
      }
    }

    const sendExamScheduledNotification = async (exam) => {
      try {
        // 시험에 연결된 클래스 정보 조회
        const classroomData = await axios({
          method: 'get',
          url: `/learning/examClassroom/${exam.examId}`,
        })
        const classroomIdx = classroomData?.data?.idx
        if (!classroomIdx) {
          console.warn('시험에 연결된 클래스가 없어 알림을 발송하지 않습니다.')
          return
        }

        // 시험 응시자에게만 알림 발송 (매핑이 없으면 백엔드가 클래스 전체로 fallback)
        await axios({
          method: 'post',
          url: '/notification/send-exam-notification/',
          data: {
            title: '시험 예정 안내',
            body: `"${exam.examName}" 시험이 예정되었습니다. 일정을 확인해주세요.`,
            classroom_idx: classroomIdx,
            exam_id: exam.examId,
            data: { examId: exam.examId },
          },
        })
        console.log('시험 예정 알림 발송 완료')
      } catch (error) {
        console.error('시험 예정 알림 발송 실패:', error)
      }
    }

    const getDropdownItems = (exam) => {
      const items = []

      // Return empty array if exam is undefined
      if (!exam) return items

      // Always show preview, copy, share
      items.push({ text: '미리보기', icon: 'visibility', action: () => openPreview(exam) })
      items.push({ text: '시험복사', icon: 'copy', action: () => copyExam(exam) })
      items.push({ text: '시험공유', icon: 'share', action: () => shareExam(exam) })

      // Status-specific items
      if (exam.status === 'draft') {
        items.push({ text: '수정', icon: 'edit', action: () => ctx.emit('edit', exam) })
        items.push({ text: '예정으로 변경', icon: 'schedule', action: () => changeStatusToScheduled(exam) })
      }

      if ((exam.status === 'scheduled' || exam.status === 'draft') && exam.settings && exam.settings.faceToFace === true) {
        items.push({ text: '시험 시작', icon: 'play_arrow', action: () => ctx.emit('start-session', exam) })
      }

      //if (exam.status === 'active') {
      //  items.push({ text: '모니터링', icon: 'visibility', action: () => ctx.emit('select', exam) })
      //}

      if (exam.status === 'completed') {
        items.push({ text: '결과 보기', icon: 'assessment', action: () => ctx.emit('view-results', exam) })
      }

      // Add divider before delete
      items.push({ divider: true })
      items.push({ text: '삭제', icon: 'delete', action: () => deleteExam(exam), danger: true })

      return items
    }

    // Lifecycle
    // 초기 데이터 fetch 는 부모(AcademicEvaluations) onMounted 에서 수행 — 중복 요청 방지

    return {
      filteredExams,
      paginatedExams,
      currentPage,
      itemsPerPage,
      totalCount,
      totalPages,
      hasData,
      isLoading,
      getStatusBadgeClass,
      statusPillKind,
      examCols,
      getTotalQuestions,
      formatDate,
      formatTime,
      formatTimeRange,
      deleteExam,
      formatExamStatus,
      // copy modal
      showCopyModal,
      copySourceExam,
      newExamName,
      isCopying,
      copyError,
      copyExam,
      closeCopyModal,
      confirmCopyExam,
      newExamNameInput,
      // share modal
      showShareModal,
      shareSourceExam,
      isSharing,
      shareError,
      shareRecipients,
      recipientEmailInput,
      shareExam,
      closeShareModal,
      confirmShareExam,
      addRecipient,
      removeRecipient,
      // preview
      showPreview,
      previewExam,
      previewVersions,
      openPreview,
      closePreview,
      changeStatusToScheduled,
      // dropdown
      getDropdownItems
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

/* ─────────────────────────────────────
   메인 리스트 (Pineapple 톤 — AcademicClasses 와 동일 패턴)
   ───────────────────────────────────── */
.p-exam-list {
  font-family: $p-font-body;
  color: $p-ink;

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 16px;
  }
  &__title {
    font-size: 13px;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: $p-ink;
  }
  &__sub {
    font-size: 11px;
    color: $p-ink-soft;
    margin-top: 2px;
  }
  &__name {
    min-width: 0;
  }
  &__name-line {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  &__name-text {
    font-size: 12px;
    font-weight: 800;
    color: $p-ink;
  }
  &__desc {
    font-size: 11px;
    color: $p-ink-soft;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 360px;
  }
  &__schedule {
    line-height: 1.4;
  }
  &__footer {
    margin: 20px -20px -20px;
    padding: 14px 20px;
    border-top: 1px solid $p-line;
    background: $p-panel-alt;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__count {
    font-size: 11px;
    color: $p-ink-soft;
  }
}

.p-mono { font-family: $p-font-mono; font-size: 11px; color: $p-ink2; }
.p-cell-sub { font-size: 11px; color: $p-ink-soft; margin-top: 2px; }
.p-icon-btn {
  width: 28px; height: 28px;
  border-radius: 6px; border: 0;
  background: transparent; color: $p-ink-mid;
  cursor: pointer;
  display: inline-grid; place-items: center;
  &:hover { background: $p-bg; color: $p-ink; }
}

.p-loader { padding: 60px 0; text-align: center; }
.p-spinner {
  display: inline-block;
  width: 36px; height: 36px;
  border: 3px solid $p-line; border-top-color: $p-point;
  border-radius: 50%;
  animation: p-spin 0.8s linear infinite;
}
.p-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: grid;
  place-items: center;
}
@keyframes p-spin { to { transform: rotate(360deg); } }

/* ─────────────────────────────────────
   ────────────────────────────────────── */

/* 공유 모달 — 시험 정보 카드 */
.p-share-card {
  padding: 14px 16px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-left: 3px solid $p-point;
  border-radius: 10px;

  &__title {
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
    margin-bottom: 4px;
  }
  &__meta {
    display: flex;
    gap: 6px;
    font-size: 11px;
    color: $p-ink-mid;
  }
}

/* 이메일 입력 + 추가 버튼 */
.p-share-input {
  display: flex;
  gap: 8px;

  .p-input { flex: 1; }
}

/* 수신자 칩 목록 */
.p-recipients {
  padding: 12px;
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-radius: 10px;
  margin-top: 12px;

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
}
.p-recipient-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px 4px 4px;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: 999px;
  font-size: 12px;
  color: $p-ink;

  &__avatar {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: linear-gradient(135deg, $p-point 0%, $p-point-deep 100%);
    color: $p-sidebar;
    display: grid;
    place-items: center;
    font-weight: 900;
    font-size: 11px;
  }
  &__email {
    padding-right: 6px;
    font-family: $p-font-mono;
    font-size: 11px;
  }
  &__x {
    width: 20px;
    height: 20px;
    border: 0;
    border-radius: 50%;
    background: $p-bg;
    color: $p-ink-mid;
    cursor: pointer;
    display: grid;
    place-items: center;
    &:hover { background: $p-err-soft; color: $p-err; }
  }
}

/* 빈 수신자 안내 */
.p-share-empty {
  padding: 32px 16px;
  text-align: center;
  background: $p-panel-alt;
  border: 1px dashed $p-line;
  border-radius: 10px;
  margin-top: 12px;

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink-mid;
  }
  &__sub {
    margin-top: 4px;
    font-size: 11px;
    color: $p-ink-soft;
  }
}

.p-alert__list {
  margin: 0;
  padding-left: 16px;
  font-size: 12px;
  color: $p-ink2;
  line-height: 1.6;
}

/* ─────────────────────────────────────
   아래는 기존 잔여 스타일 (드롭다운, 모달 내부 위젯 등 — 수정 안 함)
   ───────────────────────────────────── */
.table > :not(caption) > * > * {
  padding: 0.75rem 1.5rem;
}

.dropdown-menu {
  min-width: 160px;
}

/* 공유 상태 뱃지 스타일 */
.badge-sm {
  font-size: 0.65rem;
  padding: 0.25rem 0.5rem;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.badge-sm i {
  font-size: 10px;
}

/* Loading overlay */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
}

.exam-preview-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.exam-preview-dialog {
  width: auto;
  max-width: min(calc(210mm + 80px), 95vw);
  height: 95vh;
  background: #4a5568;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
}

/* Toolbar */
.preview-toolbar {
  background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.toolbar-left, .toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-center {
  flex: 1;
  text-align: center;
}

.preview-label {
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
}

.zoom-level {
  color: #cbd5e0;
  font-size: 0.875rem;
  min-width: 45px;
  text-align: center;
}

.btn-tool {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #cbd5e0;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-tool:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
}

.btn-tool:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-tool i {
  font-size: 20px;
}

.btn-close {
  border-color: rgba(239, 68, 68, 0.5);
  color: #fca5a5;
}

.btn-close:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: #ef4444;
}

/* Paper Container */
.paper-container {
  flex: 1;
  overflow: auto;
  padding: 20px 40px 40px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #4a5568;
  transition: transform 0.3s ease;
  transform-origin: center top;
}

/* The Actual Exam Paper */
.exam-paper {
  width: 210mm; /* A4 width */
  min-height: 297mm; /* A4 height */
  background: #ffffff;
  box-shadow: 
    0 0 0 1px rgba(0, 0, 0, 0.05),
    0 10px 30px rgba(0, 0, 0, 0.3),
    0 30px 60px rgba(0, 0, 0, 0.1);
  padding: 20mm 15mm;
  margin: 0;
  font-family: 'Nanum Myeongjo', 'Times New Roman', serif;
  color: #000;
  position: relative;
  background-image: 
    repeating-linear-gradient(
      transparent,
      transparent 28px,
      #e8e8e8 28px,
      #e8e8e8 29px
    );
}

/* Paper texture effect */
.exam-paper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    repeating-linear-gradient(
      45deg,
      transparent,
      transparent 5px,
      rgba(0, 0, 0, 0.005) 5px,
      rgba(0, 0, 0, 0.005) 10px
    );
  pointer-events: none;
}

/* Exam Header */
.exam-header {
  position: relative;
  padding-bottom: 15px;
  border-bottom: 3px double #000;
  margin-bottom: 20px;
  text-align: center;
}

.exam-institution {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 10px;
  color: #444;
}

.exam-title-box {
  margin: 15px 0;
}

.exam-main-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  letter-spacing: 2px;
  color: #000;
}

.exam-subtitle {
  font-size: 14px;
  color: #666;
  margin-top: 8px;
}

.exam-seal {
  position: absolute;
  top: 10px;
  right: 0;
  width: 80px;
  height: 80px;
  border: 3px solid #d00;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 11px;
  color: #d00;
  transform: rotate(-15deg);
  letter-spacing: 1px;
}

/* Student Information */
.student-info-section {
  margin: 25px 0;
  padding: 15px;
  border: 1px solid #333;
  background: #fafafa;
}

.student-info-table {
  width: 100%;
  border-collapse: collapse;
}

.student-info-table td {
  padding: 8px;
  vertical-align: middle;
}

.info-label {
  font-weight: 600;
  font-size: 14px;
  width: 80px;
  color: #333;
}

.info-field {
  width: 150px;
}

.fill-box {
  width: 100%;
  height: 28px;
  border: 1px solid #999;
  background: #fff;
}

/* Instructions */
.test-instructions {
  margin: 25px 0;
  padding: 15px;
  background: #f9f9f9;
  border: 1px dashed #666;
}

.instructions-title {
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 12px;
  color: #222;
}

.instructions-list {
  font-size: 13px;
  line-height: 1.8;
  color: #444;
  padding-left: 25px;
  margin: 0;
}

.instructions-list li {
  margin-bottom: 5px;
}

/* Metadata */
.exam-metadata {
  margin: 20px 0;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  padding: 15px;
  background: #f5f5f5;
  border-left: 4px solid #333;
}

.meta-item {
  text-align: center;
}

.meta-key {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.meta-val {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #000;
}

/* Main Divider */
.main-divider {
  height: 2px;
  background: #000;
  margin: 30px 0;
  position: relative;
}

.main-divider::before,
.main-divider::after {
  content: '◆';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #fff;
  padding: 0 10px;
  font-size: 14px;
}

.main-divider::before {
  left: 20px;
}

.main-divider::after {
  right: 20px;
}

/* Questions Container */
.questions-container {
  margin-top: 30px;
}

/* Question Sections */
.question-section {
  margin-bottom: 40px;
  page-break-inside: avoid;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #000;
}

.section-number {
  font-size: 24px;
  font-weight: 700;
  color: #000;
  min-width: 40px;
}

.section-info {
  flex: 1;
}

.section-name {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  color: #000;
  display: inline;
}

.section-points {
  font-size: 14px;
  color: #666;
  margin-left: 10px;
}

.section-instructions {
  margin-bottom: 15px;
  padding: 10px;
  background: #f9f9f9;
  border-left: 3px solid #666;
  font-size: 14px;
  color: #444;
  font-style: italic;
}

/* Individual Questions */
.questions {
  padding-left: 10px;
}

.question {
  display: flex;
  margin-bottom: 25px;
  page-break-inside: avoid;
}

.question-number {
  min-width: 35px;
  padding-top: 2px;
}

.q-num {
  font-weight: 700;
  font-size: 15px;
  color: #000;
}

.question-content {
  flex: 1;
}

.question-text {
  font-size: 14px;
  line-height: 1.8;
  margin-bottom: 12px;
  color: #000;
}

/* Answer Choices */
.answer-choices {
  padding-left: 20px;
  margin-top: 10px;
}

.choice-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.6;
}

.choice-marker {
  min-width: 25px;
  font-weight: 600;
  color: #000;
}

.choice-text {
  flex: 1;
  color: #222;
}

/* Answer Lines for Written Responses */
.answer-lines {
  margin-top: 15px;
  padding-left: 20px;
}

.answer-line {
  height: 30px;
  border-bottom: 1px solid #999;
  margin-bottom: 5px;
  position: relative;
}

.answer-line::before {
  content: '';
  position: absolute;
  left: -15px;
  bottom: 8px;
  width: 8px;
  height: 1px;
  background: #ccc;
}

/* Footer */
.exam-footer {
  margin-top: 60px;
  padding-top: 20px;
  border-top: 1px solid #999;
  text-align: center;
}

.page-number {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #444;
}

.footer-text {
  font-size: 12px;
  color: #666;
  font-style: italic;
}

/* Share Modal Styles */
.icon-box-sm {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-box-sm i {
  font-size: 20px;
}

.exam-info-card {
  transition: all 0.2s ease;
}

.exam-info-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.exam-icon-box {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.exam-icon-box i {
  font-size: 24px;
}

.section-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-icon i {
  font-size: 16px;
}

/* Recipient Badge Styles */
.recipients-container {
  min-height: 60px;
  border: 1px dashed #dee2e6;
}

.recipient-badge {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #dee2e6;
  border-radius: 50px;
  padding: 4px 8px 4px 4px;
  font-size: 13px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.recipient-badge:hover {
  border-color: #adb5bd;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.recipient-avatar {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  margin-right: 8px;
  flex-shrink: 0;
}

.recipient-email {
  color: #495057;
  font-weight: 500;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recipient-remove {
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: #adb5bd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 6px;
  padding: 0;
  transition: all 0.2s ease;
}

.recipient-remove:hover:not(:disabled) {
  background: #fee2e2;
  color: #dc3545;
}

.recipient-remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.recipient-remove i {
  font-size: 14px;
}

/* Empty recipients state */
.empty-recipients {
  border: 2px dashed #dee2e6;
  transition: all 0.2s ease;
}

.empty-recipients:hover {
  border-color: #adb5bd;
  background: #f1f3f4 !important;
}

/* Share info section */
.share-info-section {
  border: 1px solid #ffc107;
}

.share-info-section ul {
  list-style: disc;
}

.share-info-section li::marker {
  color: #ffc107;
}

/* Legacy styles - kept for backward compatibility */
.share-exam-info {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.share-warnings {
  padding: 15px;
  background: #fff3cd;
  border-radius: 8px;
  border: 1px solid #ffc107;
}

.share-warnings ul li {
  display: flex;
  align-items: flex-start;
}

.share-warnings ul li i {
  flex-shrink: 0;
  margin-top: 2px;
}

.share-scope-options {
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

.share-scope-options .form-check {
  padding: 12px;
  margin: 0 0 8px 0;
  border: 1px solid transparent;
  border-radius: 6px;
  transition: all 0.2s;
}

.share-scope-options .form-check:hover {
  background: #f8f9fa;
  border-color: #dee2e6;
}

.share-scope-options .form-check-input:checked ~ .form-check-label {
  color: #007bff;
  font-weight: 500;
}

.permissions-options {
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
}

.share-link-container {
  max-width: 500px;
  margin: 0 auto;
}

.share-link-container .input-group {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-radius: 8px;
  overflow: hidden;
}

.share-link-container .form-control {
  font-family: monospace;
  font-size: 14px;
}

/* Print Styles */
/* Responsive adjustments for smaller screens */
@media (max-width: 1200px) {
  .exam-preview-dialog {
    max-width: 95vw;
  }
  
  .paper-container {
    padding: 10px;
  }
  
  .exam-paper {
    transform: scale(0.8);
    transform-origin: top center;
  }
}

@media (max-width: 768px) {
  .exam-paper {
    transform: scale(0.6);
    transform-origin: top center;
  }
}

@media print {
  .exam-paper {
    box-shadow: none;
    margin: 0;
  }
  
  .preview-toolbar {
    display: none;
  }
  
  .exam-preview-modal {
    position: static;
    background: none;
  }
  
  .exam-preview-dialog {
    width: 100%;
    height: auto;
    background: none;
  }
  
  .paper-container {
    padding: 0;
    background: none;
  }
}
</style>